import React, { useEffect, useState, useCallback } from 'react';
import { Combobox, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { API } from './helpers';
import { User } from './types';

interface Props {
	selectedUsers: User[];
	onAddUser: (user: User) => void;
}

export function UserSearch({ selectedUsers, onAddUser }: Props) {
	const [searchTerm, setSearchTerm] = useState('');
	const [results, setResults] = useState<User[]>([]);
	const { translate } = useTranslate();

	const searchResults = useCallback(async () => {
		try {
			const resultUsers = await API.searchIdentity(searchTerm);

			const withoutSelectedUsers = resultUsers.filter((user) => {
				return !selectedUsers.some((selectedUser) => selectedUser.id === user.id);
			});

			if (!(withoutSelectedUsers[0]?.id === undefined)) {
				setResults(withoutSelectedUsers);
			}
		} catch (err) {
			throw new Error(`error calling endpoint: ${err}`);
		}
	}, [searchTerm, selectedUsers]);

	useEffect(() => {
		if (searchTerm.trim().length > 0) {
			searchResults();
		} else {
			setResults([]);
		}
	}, [searchTerm, searchResults]);

	return (
		<Combobox
			openOn="type"
			onChange={setSearchTerm}
			value={searchTerm}
			placeholder={translate('user-outofoffice.SearchForAUser')}
			data-testid="wf-group-management-user-search-input"
		>
			{results.length > 0 ? (
				results.map((user) => (
					<Combobox.ListItem
						onClick={() => {
							onAddUser(user);
							setSearchTerm('');
						}}
						thumbnail={
							<Thumbnail icon={user.fullName[0].toUpperCase()} shape="circle" />
						}
						key={user.id}
						isChecked={selectedUsers.includes(user)}
						subtext={user.email}
						data-testid="wf-group-management-user-search-result"
					>
						{user.fullName}
					</Combobox.ListItem>
				))
			) : (
				<Combobox.Text>No user found</Combobox.Text>
			)}
		</Combobox>
	);
}
